import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { HttpRequestHelper } from 'app/helpers/httpRequestHelper';
import { CreateConfigsReq } from 'app/interfaces/requests/Create/createConfigsReq';
import { ExposedDeleteConfigsReq } from 'app/interfaces/requests/Exposed/exposedDeleteConfigsReq';
import { ExposedUpdateAssistsConfigReq } from 'app/interfaces/requests/Exposed/exposedUpdateAssistsConfigReq';
import { ReadAssistTypesConfigReq } from 'app/interfaces/requests/Read/readAssistTypesConfigReq';
import { ExposedListDivisionDefaultValuesRes } from 'app/interfaces/responses/Exposed/exposedListDivisionDefaultValuesRes';
import { ListAssistTypesConfigRes } from 'app/interfaces/responses/List/listAssistTypesConfigRes';
import { ListConfigContextsRes } from 'app/interfaces/responses/List/listConfigContextsRes';
import { ListConfigsRes } from 'app/interfaces/responses/List/listConfigsRes';
import { ReadAssistsConfigRes } from 'app/interfaces/responses/Read/readAssistsConfigRes';
import { HttpStatus } from 'app/interfaces/support/status/httpStatus';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { TranslationService } from '../translation/translation.service';

@Injectable({
    providedIn: 'root',
})
export class ConfigsService {
    private httpRequestHelper = new HttpRequestHelper();

    constructor(
        private http: HttpClient,
        private loaderService: LoaderService,
        private alertsService: AlertsService,
        private router: Router,
        public dialog: MatDialog,
        private translationService: TranslationService
    ) {}

    //#region AssistsConfig

    public updateAssistsConfig(
        req: ExposedUpdateAssistsConfigReq,
        showError = true,
        loadingMessage = 'Uppfæri stillingar'
    ): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(loadingMessage);

        let subject = new Subject<null>();
        this.http
            .post(environment.localApi + 'configs/updateAssistsConfig', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage('Stillingar hafa verið vistaðar');
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public readAssistsConfig(req: ReadAssistTypesConfigReq, showError = true, showLoader = false): Observable<ReadAssistsConfigRes> {
        this.loaderService.changeActiveLoaders(1);
        if (showLoader) {
            this.loaderService.changeLoaderMessage('Kanna opnunartíma');
        }

        let subject = new Subject<ReadAssistsConfigRes>();
        this.http
            .post(environment.localApi + 'configs/readAssistsConfig', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<ReadAssistsConfigRes>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public listAssistTypesConfig(showError = true): Observable<Array<ListAssistTypesConfigRes>> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki gögn');

        let subject = new Subject<Array<ListAssistTypesConfigRes>>();
        this.http
            .get(environment.localApi + 'configs/listAssistTypesConfig', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ListAssistTypesConfigRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    //#endregion

    //#region Configs

    public getConfigs(): Promise<ListConfigsRes[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        return new Promise((resolve, reject) => {
            this.http
                .get(environment.localApi + 'configs/getConfigs')
                .subscribe({
                    next: (res: HttpStatus<ListConfigsRes[]>) => resolve(res.returnItem),
                    error: (error) => {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                        reject();
                    },
                })
                .add(() => {
                    this.loaderService.changeActiveLoaders(-1);
                });
        });
    }

    public updateConfigs(req: ListConfigsRes[], showError: boolean = true): Observable<null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_update_configs'));

        let subject = new Subject<null>();
        this.http
            .post(environment.localApi + 'configs/updateConfigs', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe({
                next: (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translationService.translate('info_update_configs_success'));
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(null);
                },
                error: (error) => {
                    if (showError) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    } else {
                        console.log(error);
                    }
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public createConfigs(req: CreateConfigsReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_save_data'));

        let subject = new Subject<number>();
        this.http
            .post(environment.localApi + 'configs/createConfigs', req, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<number>) => {
                    subject.next(res.returnItem);
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translationService.translate('info_register_configs_success'));
                    this.alertsService.changeAlertSuccessShow(true);
                },
                (error) => {
                    this.alertsService.changealertHeader(this.translationService.translate('error_register_configs'));
                    this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                    this.alertsService.changeAlertErrorShow(true);
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public deleteConfigs(req: ExposedDeleteConfigsReq): Observable<number> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage(this.translationService.translate('info_save_data'));

        let subject = new Subject<number>();
        this.http
            .delete(environment.localApi + `configs/DeleteConfigs/${req.id}`, this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<null>) => {
                    this.alertsService.changeAlertShowIcon(true);
                    this.alertsService.changeAlertSuccessMessage(this.translationService.translate('info_delete_configs_success'));
                    this.alertsService.changeAlertSuccessShow(true);
                    subject.next(null);
                },
                (error) => {
                    this.alertsService.changealertHeader(this.translationService.translate('error_delete_configs'));
                    this.alertsService.changeAlertErrorMessage(error.error.errorItem.message);
                    this.alertsService.changeAlertErrorShow(true);
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });
        return subject.asObservable();
    }

    public listConfigContexts(showLoader = true): Observable<Array<ListConfigContextsRes>> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        }

        let subject = new Subject<Array<ListConfigContextsRes>>();
        this.http
            .get(environment.localApi + 'configs/listConfigContexts', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ListConfigContextsRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    public getDivisionDefaultValues(showLoader = true): Observable<Array<ExposedListDivisionDefaultValuesRes>> {
        if (showLoader) {
            this.loaderService.changeActiveLoaders(1);
            this.loaderService.changeLoaderMessage(this.translationService.translate('info_fetching_information'));
        }

        let subject = new Subject<Array<ExposedListDivisionDefaultValuesRes>>();
        this.http
            .get(environment.localApi + 'configs/getDivisionDefaultValues', this.httpRequestHelper.getRequestOptionsAuth())
            .subscribe(
                (res: HttpStatus<Array<ExposedListDivisionDefaultValuesRes>>) => {
                    subject.next(res.returnItem);
                },
                (error) => {
                    subject.next(null);
                    console.log(error);
                }
            )
            .add(() => {
                if (showLoader) {
                    this.loaderService.changeActiveLoaders(-1);
                }
            });
        return subject.asObservable();
    }

    //#endregion
}
